import { Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import { FormEvent, useCallback, useMemo, useState } from "react";
import {
  useCreateQuestionMutation,
  useGetQuestionsQuery,
} from "src/api/services/questionsApi";
import { Column, InputType, Question, QuestionInsert } from "src/api/types";
import DataTable from "src/components/DataTable";
import FormInput from "src/components/FormInput";
import Filter from "src/components/TableFilter";
import { useFromAndTo } from "src/hooks/useFromAndTo";
import { useManageDrawer } from "src/hooks/useManageDrawer";
import { usePagination } from "src/hooks/usePagination";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import DrawerForm from "src/shared/DrawerForm";
import { filters } from "./utils/filters";
import { useSearchParams } from "react-router-dom";
import { TITLE } from "src/constants/searchParams";

const types = [
  { value: "height_weight", label: "Height/Weight" },
  { value: "progress-projection", label: "Progress Projection" },
  { value: "cancellation_review", label: "Cancellation Review" },
  { value: "cancellation_message", label: "Cancellation Message" },
  { value: "ed_options", label: "ED Options" },
  { value: "multiple_choice", label: "Multiple Choice" },
  { value: "state-disqualification", label: "State Disqualification" },
  { value: "number", label: "Number" },
  { value: "glp1_reviews", label: "GLP-1 Reviews" },
  { value: "payment-information", label: "Payment Information" },
  { value: "glp1_preliminary_results", label: "GLP-1 Preliminary Results" },
  { value: "message_image", label: "Message Image" },
  { value: "payment-confirmation", label: "Payment Confirmation" },
  { value: "single_choice", label: "Single Choice" },
  { value: "text", label: "Text" },
  { value: "id-verification", label: "ID Verification" },
  { value: "shipping-information", label: "Shipping Information" },
  { value: "disqualification", label: "Disqualification" },
  { value: "message", label: "Message" },
  { value: "birthday", label: "Birthday" },
  { value: "preferred-pharmacy", label: "Preferred Pharmacy" },
];

const useInputs = () => {
  const [values, setValues] = useState<QuestionInsert>({
    title: "",
    subtitle: "",
    required_answer: true,
    follow_up: false,
    question_type: "text",
    tag: null,
    slug: "",
  });

  const setFields = useCallback(
    <K extends keyof QuestionInsert>(key: K, value: QuestionInsert[K]) => {
      setValues((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const inputs: InputType<QuestionInsert>[] = useMemo(
    () => [
      {
        type: "text",
        name: "title",
        label: "Title",
        required: true,
        value: values.title ?? "",
        onChange: (v) => setFields("title", v ?? ""),
      },
      {
        type: "text",
        name: "subtitle",
        label: "Subtitle",
        required: false,
        value: values.subtitle ?? "",
        onChange: (v) => setFields("subtitle", v ?? ""),
      },
      {
        type: "text",
        name: "slug",
        label: "Slug",
        required: false,
        disabled: true,
        value: values.slug ?? "",
        onChange: (v) => setFields("slug", v ?? ""),
      },
      {
        type: "select",
        name: "question_type",
        label: "Question Type",
        required: true,
        value: values.question_type ?? "text",
        options: types,
        onChange: (v) =>
          setFields(
            "question_type",
            (v ?? "text") as Question["question_type"],
          ),
      },
      {
        type: "text",
        name: "tag",
        label: "Tag",
        required: false,
        value: values.tag ?? "",
        onChange: (v) => setFields("tag", v ?? null),
      },
      {
        type: "checkbox",
        name: "required_answer",
        label: "Required Answer",
        required: false,
        value: values.required_answer ?? true,
        onChange: (v) => setFields("required_answer", v),
      },
      {
        type: "checkbox",
        name: "follow_up",
        label: "Follow Up",
        required: false,
        value: values.follow_up ?? false,
        onChange: (v) => setFields("follow_up", v),
      },
    ],
    [setFields, values],
  );

  return { values, inputs, setValues };
};

const useColumns = (): Column<Question>[] => {
  return [
    {
      id: "title",
      label: "Title",
      orderable: false,
      minWidth: 300,
      align: "center",
      format: (row) => row.title,
      render: (row) => <Typography>{row.title}</Typography>,
    },
    {
      id: "subtitle",
      label: "Subtitle",
      orderable: false,
      minWidth: 300,
      align: "center",
      format: (row) => row.subtitle || "",
      render: (row) => <Typography>{row.subtitle || "N/A"}</Typography>,
    },
    {
      id: "required",
      label: "Required",
      orderable: false,
      align: "center",
      format: (row) => row.required_answer.toString(),
      render: (row) => (
        <Typography>{row.required_answer ? "✔️" : ""}</Typography>
      ),
    },
    {
      id: "follow_up",
      label: "Follow Up",
      orderable: false,
      align: "center",
      format: (row) => row.follow_up.toString(),
      render: (row) => <Typography>{row.follow_up ? "✔️" : ""}</Typography>,
    },
    {
      id: "type",
      label: "Type",
      orderable: false,
      minWidth: 100,
      align: "center",
      format: (row) => row.question_type,
      render: (row) => (
        <Typography textTransform="capitalize">{row.question_type}</Typography>
      ),
    },
    {
      id: "tag",
      label: "Tag",
      orderable: false,
      minWidth: 100,
      align: "center",
      format: (row) => row.tag || "",
      render: (row) => <Typography>{row.tag || "N/A"}</Typography>,
    },
  ];
};

const Questions = () => {
  const { inputs, values } = useInputs();
  const [createQuestion, { isLoading }] = useCreateQuestionMutation();
  const [open, { handleOpen, handleClose }] = useManageDrawer();
  const columns = useColumns();
  const logErr = useSentryLogger("error");
  const height = useMemo(() => window.innerHeight - 104 - 52 - 56, []);
  const pagination = usePagination();
  const { from, to } = useFromAndTo();
  const [searchParams] = useSearchParams();

  const queryParams = useMemo(
    () => ({
      from,
      to,
      title: searchParams.get(TITLE) ?? undefined,
    }),
    [from, searchParams, to],
  );

  const { data, isFetching } = useGetQuestionsQuery(queryParams);

  const { questions, total } = useMemo(() => {
    if (data == null) return { questions: [], total: 0 };

    return { questions: data.data, total: data.count };
  }, [data]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      try {
        await createQuestion(values).unwrap();
        handleClose();
      } catch (error) {
        logErr("Failed to create question", error);
      }
    },
    [createQuestion, handleClose, logErr, values],
  );

  return (
    <>
      <Stack gap={2} sx={{ width: "100%", minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5">Questions</Typography>
          <Button onClick={handleOpen}>+ Add Question</Button>
        </Stack>

        <Stack direction="row" gap="16px" flexWrap="wrap">
          {filters.map((filter) => (
            <Filter key={filter.name} current={filter} />
          ))}
        </Stack>

        <Stack gap={0} sx={{ width: "100%", minWidth: 0 }}>
          <Box height={4}>{isFetching ? <LinearProgress /> : null}</Box>
          <DataTable
            data={questions}
            columns={columns}
            height={height}
            primaryKey="id"
            pagination={pagination}
            total={total}
          />
        </Stack>
      </Stack>
      <DrawerForm
        open={open}
        loading={isLoading}
        label="Add Question"
        handleSubmit={handleSubmit}
        onClose={handleClose}
      >
        <Stack gap={3}>
          {inputs.map((input) => (
            <FormInput key={input.label} current={input} />
          ))}
        </Stack>
      </DrawerForm>
    </>
  );
};

export default Questions;
